import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { DmTriageStatus } from '@evo-hub/shared';

export type TriageStatus = DmTriageStatus;

export interface TriageRow {
  channelId: number;
  platform: string | null;
  senderDisplayName: string | null;
  lastMessageBody: string | null;
  lastMessageAt: string | null;
  phishingFlag: boolean;
  contactId?: number;
  contactName?: string | null;
  contactDecision?: string | null;
}

export function useTriage(status: TriageStatus) {
  return useQuery<{ rows: TriageRow[] }>({
    queryKey: ['dm-triage', status],
    queryFn: () => api.get<{ rows: TriageRow[] }>(`/api/dm-triage?status=${status}`),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useDismissChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelIds: number[]) => api.post<{ dismissed: number }>('/api/dm-triage/dismiss', { channelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}

export function useUndismissChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelIds: number[]) => api.post<{ restored: number }>('/api/dm-triage/undismiss', { channelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}

export function useLinkToContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelIds, contactId }: { channelIds: number[]; contactId: number }) =>
      api.post<{ linked: number }>('/api/dm-triage/link', { channelIds, contactId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}

export function useCreateContactsFromTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelIds: number[]) =>
      api.post<{ created: Array<{ channelId: number; contactId: number }> }>('/api/dm-triage/create-contact', { channelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}

export function useCreateLeadsFromTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelIds: number[]) =>
      api.post<{ created: Array<{ channelId: number; contactId: number; leadId: string }> }>('/api/dm-triage/create-lead', { channelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}

export function useUnlinkChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelIds: number[]) => api.post<{ unlinked: number }>('/api/dm-triage/unlink', { channelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm-triage'] }),
  });
}
